import {
   AfterViewInit,
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   OnDestroy,
   OnInit,
   ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { WorkflowTasksStoreService } from '../../../services/stores/workflow-tasks-store.service';
import { DayCell } from '../../../domain/day-cell';
import { DateTime } from 'luxon';
import { DailyTaskDetail } from '../../../domain/daily-task-detail';
import { Router } from '@angular/router';
import { WorkflowStoreService } from '../../../services/stores/workflow-store.service';
import { Subscription, take, firstValueFrom } from 'rxjs';
import { PermissionsStoreService } from '../../../services/stores/permission-store-service';
import { UserPermissions } from '../../../domain/security/user-permissions';
import { DateLocalFormatPipe } from '../../../pipes/date-local-format.pipe';
import { DashboardCardComponent } from '../dashboard-card/dashboard-card.component';

@Component({
   selector: 'app-my-claimed-tasks',
   changeDetection: ChangeDetectionStrategy.OnPush,
   standalone: true,
   imports: [
      CommonModule,
      ButtonModule,
      TagModule,
      CalendarModule,
      FormsModule,
      DateLocalFormatPipe,
      DashboardCardComponent
   ],
   templateUrl: './my-claimed-tasks.component.html',
   styleUrls: ['./my-claimed-tasks.component.scss']
})
export class MyClaimedTasksComponent implements OnInit, AfterViewInit, OnDestroy {
   claimedTasks: DayCell[] = [];
   selectedDate: Date = new Date();
   isLoading: boolean = false;
   maxDate: Date = new Date();
   visibleStartIndex: number = 0;
   tilesInView: number = 0;
   dataLoaded: boolean = false;
   userPermissions: UserPermissions;
   @ViewChild('tileContainer', { static: false })
   tileContainerRef!: ElementRef<HTMLDivElement>;
   private readonly tilesInViewKey = 'my-claimed-tasks-tiles-in-view';
   private subs = new Subscription();
   private readonly onResize = () => this.calculateVisibleTiles();

   constructor(
      public workFlowTasksStore: WorkflowTasksStoreService,
      private workflowStore: WorkflowStoreService,
      private permissionsStore: PermissionsStoreService,
      private router: Router,
      private cdr: ChangeDetectorRef
   ) {
      this.isLoading = true;
      this.userPermissions = new UserPermissions();
   }

   async ngOnInit() {
      this.subs.add(
         this.workflowStore.lastWorkflowRunDate.subscribe((runDateStr) => {
            const anchor = DateTime.fromISO(runDateStr);
            this.selectedDate = anchor.toJSDate();
            this.maxDate = anchor.toJSDate();

            this.setMonthAndLoadTasks(anchor);
         })
      );

      this.userPermissions = await firstValueFrom(this.permissionsStore.get());
      this.cdr.markForCheck();

      this.subs.add(
         this.workFlowTasksStore.claimedTaskRefresh$.subscribe(() => {
            this.onDateChange();
         })
      );

      this.subs.add(
         this.workflowStore.isLoading.subscribe((loading) => {
            this.claimedTasks = [];
            this.isLoading = loading;
         })
      );

      this.subs.add(
         this.workFlowTasksStore.myDailyTasksBySelectedDate.subscribe((data) => {
            const monthStart = DateTime.fromJSDate(this.selectedDate).startOf(
               'month'
            );
            const monthEnd = DateTime.fromJSDate(this.selectedDate).endOf('month');

            const mappedTasks: DayCell[] = data.map((task) => {
               const cell = new DayCell(task);
               cell.start = task.taskDate;
               cell.extendedProps = { ...cell.extendedProps, ...task };
               return cell;
            });

            this.claimedTasks = mappedTasks.filter((task) => {
               const taskDate = DateTime.fromISO(task.extendedProps.taskDate);
               const isInSameMonth = taskDate >= monthStart && taskDate <= monthEnd;
               const hasSomeData =
                  (task.extendedProps.dailyCount ?? 0) > 0 ||
                  (task.extendedProps.nonDailyCount ?? 0) > 0 ||
                  (task.extendedProps.formCount ?? 0) > 0;
               return isInSameMonth && hasSomeData;
            });

            this.isLoading = false;
            this.dataLoaded = true;

            this.calculateVisibleTiles();
            this.centerSelectedTask();

            this.cdr.markForCheck();
         })
      );
   }

   ngOnDestroy() {
      window.removeEventListener('resize', this.onResize);

      this.subs.unsubscribe();
   }

   ngAfterViewInit() {
      const defaultTiles = this.calculateDefaultTilesInView();
      const savedTiles = localStorage.getItem(this.tilesInViewKey);

      if (savedTiles) {
         const savedValue = parseInt(savedTiles, 10);
         this.tilesInView = Math.max(savedValue, defaultTiles);
      } else {
         this.tilesInView = defaultTiles;
      }

      this.calculateVisibleTiles();

      window.addEventListener('resize', this.onResize);
   }

   addTransitionEffect() {
      if (this.tileContainerRef && this.tileContainerRef.nativeElement) {
         const container = this.tileContainerRef.nativeElement;
         container.classList.add('fade-transition');
         setTimeout(() => container.classList.remove('fade-transition'), 300);
      }
   }

   calculateVisibleTiles() {
      if (this.tileContainerRef) {
         const containerWidth = this.tileContainerRef.nativeElement.offsetWidth;
         const tileWidth = 120 + 24; // tile width + margin
         const defaultTiles = Math.floor(containerWidth / tileWidth);

         // If tilesInView hasn’t been set yet, use the default
         if (!this.tilesInView || this.tilesInView < defaultTiles) {
            this.tilesInView = defaultTiles;
         }
      }
   }

   setMonthAndLoadTasks(dateTime: DateTime) {
      const monthStartDate = dateTime.startOf('month').toJSDate();
      this.refreshAndLoadMonthTasks(monthStartDate);
   }

   refreshAndLoadMonthTasks(date: Date) {
      this.isLoading = true;
      this.dataLoaded = false;

      this.workFlowTasksStore.refreshMyTasksForMonth(date);

      const monthStart = DateTime.fromJSDate(date).startOf('month');
      const monthEnd = DateTime.fromJSDate(date).endOf('month');

      this.workFlowTasksStore.myDailyTasksBySelectedDate
         .pipe(take(1)) // Wait for **new emission only**
         .subscribe({
            next: (data: DailyTaskDetail[]) => {
               const mappedTasks: DayCell[] = data.map((task) => {
                  const cell = new DayCell(task);
                  cell.start = task.taskDate;
                  cell.extendedProps = { ...cell.extendedProps, ...task };
                  return cell;
               });

               this.claimedTasks = mappedTasks.filter((task) => {
                  const taskDate = DateTime.fromISO(task.extendedProps.taskDate);
                  const isInSameMonth =
                     taskDate >= monthStart && taskDate <= monthEnd;
                  const hasSomeData =
                     (task.extendedProps.dailyCount ?? 0) > 0 ||
                     (task.extendedProps.nonDailyCount ?? 0) > 0 ||
                     (task.extendedProps.formCount ?? 0) > 0;

                  return isInSameMonth && hasSomeData;
               });

               this.isLoading = false;
               this.dataLoaded = true;

               this.calculateVisibleTiles();
               this.centerSelectedTask();

               this.cdr.markForCheck();
            },
            error: (err) => {
               console.error(err);
               this.isLoading = false;
            }
         });
   }

   onDateChange() {
      this.isLoading = true;
      this.claimedTasks = [];
      this.dataLoaded = false;

      const selectedMonthStart = DateTime.fromJSDate(this.selectedDate)
         .startOf('month')
         .toJSDate();
      this.refreshAndLoadMonthTasks(selectedMonthStart);
      this.addTransitionEffect();
   }

   async onTaskClick(task: DayCell) {
      // Prevent navigation during Cypress tests
      if ((window as any).Cypress) return;

      await this.router.navigate(['tasklist'], {
         queryParams: {
            from: this.router.url,
            listType: 1,
            selecteddate: task.start
         }
      });
   }
   private getClosestAvailableDate(): Date | null {
      if (!this.claimedTasks.length) return null;

      const selected = DateTime.fromJSDate(this.selectedDate);

      let closestTask = this.claimedTasks[0];
      let closestDiff = Math.abs(
         DateTime.fromISO(closestTask.start).diff(selected, 'days').days
      );

      for (const task of this.claimedTasks) {
         const taskDate = DateTime.fromISO(task.start);
         const diff = Math.abs(taskDate.diff(selected, 'days').days);

         if (diff < closestDiff) {
            closestDiff = diff;
            closestTask = task;
         }
      }

      return DateTime.fromISO(closestTask.start).toJSDate();
   }

   isSelectedTask(task: DayCell): boolean {
      const selectedTaskExists = this.claimedTasks.some(
         (t) =>
            DateTime.fromISO(t.start).toISODate() ===
            DateTime.fromJSDate(this.selectedDate).toISODate()
      );

      const effectiveDate = selectedTaskExists
         ? this.selectedDate
         : this.getClosestAvailableDate();

      return (
         DateTime.fromISO(task.start).toISODate() ===
         DateTime.fromJSDate(effectiveDate!).toISODate()
      );
   }

   increaseTilesInView() {
      const defaultTiles = this.calculateDefaultTilesInView();
      if (this.tilesInView < this.claimedTasks.length) {
         const newTiles = Math.min(
            this.tilesInView + defaultTiles,
            this.claimedTasks.length
         );

         const centerIndex =
            this.visibleStartIndex + Math.floor(this.tilesInView / 2);
         this.tilesInView = newTiles;
         this.visibleStartIndex = Math.max(
            0,
            Math.min(
               centerIndex - Math.floor(this.tilesInView / 2),
               this.claimedTasks.length - this.tilesInView
            )
         );
         this.addTransitionEffect();
         this.saveTilesInViewPreference();
      }
   }

   decreaseTilesInView() {
      const defaultTiles = this.calculateDefaultTilesInView();
      if (this.tilesInView > defaultTiles) {
         const newTiles = Math.max(this.tilesInView - defaultTiles, defaultTiles);

         const centerIndex =
            this.visibleStartIndex + Math.floor(this.tilesInView / 2);
         this.tilesInView = newTiles;
         this.visibleStartIndex = Math.max(
            0,
            Math.min(
               centerIndex - Math.floor(this.tilesInView / 2),
               this.claimedTasks.length - this.tilesInView
            )
         );
         this.addTransitionEffect();
         this.saveTilesInViewPreference();
      }
   }

   calculateDefaultTilesInView(): number {
      if (this.tileContainerRef) {
         const containerWidth = this.tileContainerRef.nativeElement.offsetWidth;
         const tileWidth = 120 + 24;
         return Math.floor(containerWidth / tileWidth);
      }
      return 1;
   }

   saveTilesInViewPreference() {
      localStorage.setItem(this.tilesInViewKey, this.tilesInView.toString());
   }

   moveLeft() {
      this.visibleStartIndex = Math.max(0, this.visibleStartIndex - 1);
      this.addTransitionEffect();
   }

   moveRight() {
      if (this.visibleStartIndex + this.tilesInView < this.claimedTasks.length) {
         this.visibleStartIndex += 1;
         this.addTransitionEffect();
      }
   }

   centerSelectedTask() {
      if (!this.claimedTasks?.length) return;

      const selectedDayStr = DateTime.fromJSDate(this.selectedDate).toISODate();

      const selectedIndex = this.claimedTasks.findIndex(
         (task) => DateTime.fromISO(task.start).toISODate() === selectedDayStr
      );

      if (selectedIndex >= 0 && this.tilesInView > 0) {
         const centerIndex = Math.max(
            0,
            selectedIndex - Math.floor(this.tilesInView / 2)
         );
         this.visibleStartIndex = Math.min(
            centerIndex,
            Math.max(0, this.claimedTasks.length - this.tilesInView)
         );
      }
   }

   goToTaskCalendar() {
      this.router.navigate(['/tasks']);
   }
}
